import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  GraduationCap, Check, X, Download, RefreshCw, 
  Image, Tag, User, Clock, Filter
} from 'lucide-react';

const TrainingSuggestionsManager = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, exported: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, approved, exported
  const [exporting, setExporting] = useState(false);

  const fetchSuggestions = useCallback(async () => {
    setLoading(true);
    try {
      const status = filter === 'all' ? '' : filter;
      const res = await axios.get(`${API}/visual-search/training-suggestions${status ? `?status=${status}` : ''}`, { withCredentials: true });
      setSuggestions(res.data.suggestions);
      setStats(res.data.stats);
    } catch (err) {
      console.error('Failed to fetch suggestions:', err);
      toast.error('Failed to load training suggestions');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  const approveSuggestion = async (suggestionId) => {
    try {
      await axios.put(`${API}/visual-search/training-suggestions/${suggestionId}/approve`, {}, { withCredentials: true });
      toast.success('Suggestion approved');
      fetchSuggestions();
    } catch (err) {
      toast.error('Failed to approve suggestion');
    }
  };

  const exportTrainingData = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/visual-search/training-export`, { withCredentials: true });
      
      if (res.data.export_count === 0) {
        toast.info('No approved suggestions to export');
        return;
      }
      
      // Create downloadable JSON file
      const dataStr = JSON.stringify(res.data.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `training_data_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${res.data.export_count} training images`);
      fetchSuggestions();
    } catch (err) {
      toast.error('Failed to export training data');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="training-suggestions-manager">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <GraduationCap className="w-5 h-5" />
            AI Training Suggestions
          </h2>
          <p className="text-sm text-muted-foreground">
            Review and export user suggestions to improve the visual search model
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            onClick={exportTrainingData}
            disabled={exporting || stats.approved === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exporting...' : `Export Approved (${stats.approved})`}
          </Button>
          <Button variant="ghost" size="icon" onClick={fetchSuggestions}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-muted-foreground">Total</p>
          <p className="text-2xl font-bold">{stats.total}</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
          <p className="text-sm text-amber-600">Pending Review</p>
          <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <p className="text-sm text-green-600">Approved</p>
          <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
        </div>
        <div className="bg-[var(--gold)]/10 rounded-lg p-4 border border-[var(--gold)]/30">
          <p className="text-sm text-[var(--gold)]">Exported</p>
          <p className="text-2xl font-bold text-[var(--gold)]">{stats.exported}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <div className="flex gap-2">
          {['all', 'pending', 'approved', 'exported'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                filter === f 
                  ? 'bg-stone-900 text-white' 
                  : 'bg-secondary hover:bg-secondary/80'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Suggestions List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : suggestions.length === 0 ? (
        <div className="text-center py-12 bg-secondary/30 rounded-lg">
          <GraduationCap className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No training suggestions yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Suggestions submitted from Visual Search will appear here
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {suggestions.map((sug) => (
            <div 
              key={sug.suggestion_id} 
              className={`bg-white border rounded-lg p-4 ${
                sug.status === 'pending' ? 'border-amber-200' :
                sug.status === 'approved' ? 'border-green-200' :
                'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      sug.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                      sug.status === 'approved' ? 'bg-green-100 text-green-700' :
                      'bg-[var(--gold)]/20 text-[var(--gold)]'
                    }`}>
                      {sug.status}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(sug.created_at)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 mb-2">
                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4 text-muted-foreground" />
                      <span className="font-semibold">{sug.suggested_material_name}</span>
                    </div>
                    {sug.suggested_material_type && (
                      <span className="text-sm text-muted-foreground">
                        Type: {sug.suggested_material_type}
                      </span>
                    )}
                  </div>
                  
                  {sug.notes && (
                    <p className="text-sm text-muted-foreground mb-2">
                      Notes: {sug.notes}
                    </p>
                  )}
                  
                  <div className="text-xs text-muted-foreground flex items-center gap-1">
                    <User className="w-3 h-3" />
                    Submitted by: {sug.submitted_by_email}
                  </div>
                </div>

                {/* Actions */}
                {sug.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => approveSuggestion(sug.suggestion_id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div className="bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-lg p-4">
        <h4 className="font-medium text-[var(--gold)] mb-2">How to use training data</h4>
        <ol className="text-sm text-white/70 space-y-1 list-decimal list-inside">
          <li>Review and approve correct suggestions</li>
          <li>Click "Export Approved" to download training data</li>
          <li>Upload the exported images to Roboflow with their labels</li>
          <li>Retrain your model with the new data</li>
          <li>Update the model version in the code</li>
        </ol>
      </div>
    </div>
  );
};

export default TrainingSuggestionsManager;
